import React, { useState, useEffect } from 'react';
import { Outlet, Link, useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase-client';
import { LogOut, Settings, Bot, TrendingUp } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLanguage } from '../lib/i18n';

export default function Layout() {
  const navigate = useNavigate();
  const [isAdmin, setIsAdmin] = useState(false);
  const { t } = useLanguage();

  useEffect(() => {
    checkAuth();
  }, []);

  const checkAuth = async () => {
    try {
      const { data: { session }, error } = await supabase.auth.getSession();
      
      if (error || !session) {
        navigate('/login', { replace: true });
        return;
      }

      // Check admin status
      const { data: profile } = await supabase
        .from('profiles')
        .select('is_admin')
        .eq('id', session.user.id)
        .single();
        
      setIsAdmin(profile?.is_admin || false);

      // If not admin and trying to access admin page, redirect to dashboard
      if (!profile?.is_admin && window.location.pathname.includes('/admin')) {
        navigate('/dashboard', { replace: true });
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      navigate('/login', { replace: true });
    }
  };

  const handleSignOut = async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) throw error;
      
      localStorage.removeItem('supabase.auth.token');
      toast.success('Logged out successfully');
      navigate('/', { replace: true });
    } catch (error: any) {
      console.error('Error signing out:', error);
      toast.error('Failed to sign out');
      navigate('/login', { replace: true });
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-blue-900 via-blue-700 to-blue-500">
      <header className="bg-black/90 shadow-lg">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between h-14 md:h-16 items-center px-4">
            <div className="flex items-center space-x-4">
              <Link to="/" className="flex-shrink-0 flex items-center group">
                <Bot className="h-5 w-5 md:h-6 md:w-6 text-white group-hover:text-cyan-400 transition-colors" />
                <div className="flex items-baseline">
                  <span className="ml-2 text-lg md:text-xl font-semibold text-white group-hover:text-cyan-400 transition-colors">ÁiX</span>
                  <span className="ml-2 text-xs text-gray-400">Beta202502</span>
                </div>
              </Link>
              <div className="hidden md:flex space-x-4">
                <Link
                  to="/trading-bots"
                  className="flex items-center text-white hover:text-cyan-400 transition-colors text-sm md:text-base"
                >
                  <TrendingUp className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                  <span>{t('nav.tradingBots')}</span>
                </Link>
                {isAdmin && (
                  <Link
                    to="/admin"
                    className="flex items-center text-white hover:text-cyan-400 transition-colors text-sm md:text-base"
                  >
                    <Bot className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                    <span>Dashboard</span>
                  </Link>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-4">
              <Link
                to="/settings"
                className="flex items-center text-white hover:text-cyan-400 transition-colors text-sm md:text-base"
              >
                <Settings className="h-4 w-4 md:h-5 md:w-5 mr-2" />
                <span className="hidden md:inline">Settings</span>
              </Link>
              <button
                onClick={handleSignOut}
                className="flex items-center text-sm text-blue-400 hover:text-blue-300 transition-colors"
              >
                <LogOut className="h-4 w-4 mr-1" />
                <span className="hidden md:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="flex-grow container mx-auto max-w-7xl py-6 px-4">
        <Outlet />
      </main>

      <footer className="bg-black/90 backdrop-blur-sm text-white mt-auto">
        <div className="max-w-7xl mx-auto py-4 px-4">
          <div className="flex flex-col space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div>
                <h3 className="text-sm font-semibold mb-3">{t('footer.organization')}</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/about" className="text-xs hover:text-blue-300 transition-colors">
                      {t('footer.about')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/faq" className="text-xs hover:text-blue-300 transition-colors">
                      {t('footer.faq')}
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-3">{t('footer.legal')}</h3>
                <ul className="space-y-2">
                  <li>
                    <Link to="/privacy-policy" className="text-xs hover:text-blue-300 transition-colors">
                      {t('footer.privacy')}
                    </Link>
                  </li>
                  <li>
                    <Link to="/terms-of-service" className="text-xs hover:text-blue-300 transition-colors">
                      {t('footer.terms')}
                    </Link>
                  </li>
                </ul>
              </div>

              <div>
                <h3 className="text-sm font-semibold mb-3">{t('footer.social')}</h3>
                <ul className="space-y-2">
                  <li>
                    <a 
                      href="https://t.me/teamiccc" 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="text-xs hover:text-blue-300 transition-colors"
                    >
                      {t('footer.telegram')}
                    </a>
                  </li>
                </ul>
              </div>
            </div>

            <div className="flex justify-center border-t border-gray-800 pt-4 mt-4">
              <p className="text-xs text-gray-500">© 2025 AIX. {t('footer.rights')}</p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
